import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Decoration } from '../../../../../../lookbook-maestro/src/graphql';
import {
  debounceTime,
  first,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, from, of, Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { FontLoaderService } from 'ZalamoLibrary/src/lib/ui/fonts-display/font-loader.service';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import cuid from 'cuid/index';
import { IMAGE_MANAGEMENT_URL } from 'ZalamoLibrary/src/lib/zalamo/photo-manager/consts';

@Component({
    selector: 'lib-fancy-graphic-drawer',
    templateUrl: './fancy-graphic-drawer.component.html',
    styleUrls: ['./fancy-graphic-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FancyGraphicDrawerComponent
  extends AliveState
  implements OnInit, OnChanges
{
  @ViewChild('svgContainer', { static: true })
  svgContainer: ElementRef<HTMLElement>;
  svgBody$ = new BehaviorSubject(null);
  fontSizes = {};
  @Output() svgLoaded = new EventEmitter();
  @Output() fontLoaded = new EventEmitter();
  @Input() userData: {
    firstName: string;
    lastName: string;
    personalizationMode: 'custom' | 'fromType';
    lines: string[];
  };
  runTextFieldUpdate$ = new Subject();
  _decoration: Decoration;

  @Input() set decoration(decoration: Decoration) {
    if (!decoration) {
      return;
    }
    const decorationChanged =
      decoration?.decorationId !== this._decoration?.decorationId;
    this._decoration = decoration;

    if (this.svgBody$.value !== null && !decorationChanged) {
      this.updateTextFields();
      return;
    }

    if (!svgCache[decoration.decorationId]) {
      svgCache[decoration.decorationId] = this.httpClient
        .get(
          `${this.imagesUrl}/public-image`,

          {
            params: {
              type: 'svg',
              fileName: decoration.fileName,
              organizationId: 'zalamo',
              size: '500',
            },
            withCredentials: false,
            responseType: 'text',
          }
        )
        .pipe(shareReplay(1));
    }
    svgCache[decoration.decorationId].pipe(first()).subscribe((res) => {
      this.svgBody$.next(
        this.domSanitizer.bypassSecurityTrustHtml(
          res
            .replace(/fnt0/g, cuid())
            .replace(/fnt1/g, cuid())
            .replace(/fnt2/g, cuid())
            .replace(/fill0/g, cuid())
            .replace(/fill1/g, cuid())
            .replace(/fill2/g, cuid())
            .replace(/fil0/g, cuid())
            .replace(/fil1/g, cuid())
            .replace(/fil2/g, cuid())
        )
      );
      this.cd.detectChanges();
    });
  }

  _sampleTexts: Array<string> = [];

  @Input() set sampleTexts(texts: Array<string>) {
    if (this._sampleTexts.join(',') !== texts.join(',')) {
      this._sampleTexts = texts;
    }
    // console.log(texts);
  }

  constructor(
    private httpClient: HttpClient,
    @Inject(IMAGE_MANAGEMENT_URL) private imagesUrl: string,
    private domSanitizer: DomSanitizer,
    private organizationService: OrganizationService,
    private fontLoaderService: FontLoaderService,
    private cd: ChangeDetectorRef,
    private ref: ElementRef
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      Object.keys(changes).length === 1 &&
      Object.keys(changes)[0] === 'sampleTexts'
    ) {
      if (
        changes.sampleTexts.currentValue.join(',') ===
        changes.sampleTexts.previousValue.join(',')
      ) {
        return;
      }
    }
    super.ngOnChanges(changes);
    this.updateTextFields();
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.runTextFieldUpdate$.pipe(
        debounceTime(100),
        tap(() => this.realUpdateTextFields())
      ),
      // this.runTextFieldUpdate$.pipe(
      //   throttleTime(100),
      //   tap(() => this.realUpdateTextFields())
      // ),
      this.svgBody$.pipe(
        RxjsOperators.filterTruthy(),
        switchMap(() =>
          from(
            new Promise<void>((resolve) => {
              const check = () => {
                if (this.svgContainer.nativeElement.querySelector('svg')) {
                  resolve();
                } else {
                  setTimeout(() => check(), 10);
                }
              };
              check();
            })
          )
        ),
        tap((svgBody) => {
          this.cd.detectChanges();
        })
      ),
      this.svgBody$.pipe(
        RxjsOperators.filterTruthy(),
        switchMap(() =>
          from(
            new Promise<void>((resolve) => {
              const check = () => {
                if (this.svgContainer.nativeElement.querySelector('svg')) {
                  resolve();
                } else {
                  setTimeout(() => check(), 10);
                }
              };
              check();
            })
          )
        ),
        first(),
        tap((svgBody) => {
          this.updateTextFields();
        })
      )
    );
  }

  realUpdateTextFields() {
    // console.log('update text fields');
    const allElements =
      this.svgContainer.nativeElement.querySelectorAll<SVGGraphicsElement>(
        'text,path'
      );
    const textContainers =
      this.svgContainer.nativeElement.querySelectorAll<SVGTextElement>('text');

    const svg = this.svgContainer.nativeElement.querySelector('svg');
    const textConfig = JSON.parse(this._decoration.config)?.fields;

    if (textContainers.length === 0) {
      if (textConfig.length) {
        setTimeout(() => this.updateTextFields(), 100);
      }
      console.log('MARK SVG AS LOADED');

      this.fontLoaded.emit();
      this.svgLoaded.emit();

      return false;
    }
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    const largerLength = Math.max(
      Math.round(svg.viewBox.baseVal.width),
      Math.round(svg.viewBox.baseVal.height)
    );
    let minFont = 999999;
    const userData = this.userData;
    const allPreparations = [];
    textConfig.forEach((value, index) => {
      const preparations = [of(null)];
      const textElement = textContainers.item(index);
      const textPath = textElement.querySelector('textPath');
      const textContainer = textPath ? textPath : textElement;
      // console.log(textContainer.nodeName);
      if (value.font?.fileName) {
        preparations.push(
          this.organizationService.getCurrentOrganization().pipe(
            first(),
            switchMap((organizationId) =>
              this.fontLoaderService.loadFont(
                value.font.fontId,
                value.font.fileName,
                organizationId
              )
            ),
            tap(() => {
              textElement.style.fontFamily = `'${value.font.fontId}', 'fallback' `;
            })
          )
        );
      } else {
      }
      preparations.forEach((v) => allPreparations.push(v));
      console.log(preparations);
      combineLatest(preparations).subscribe(() => {
        if (value.sampleText) {
          textContainer.innerHTML = value.sampleText;
        }
        // console.log(this._sampleTexts);
        if (this._sampleTexts[index]) {
          textContainer.innerHTML = this._sampleTexts[index];
        }
        if (typeof this.fontSizes[index] === 'undefined') {
          this.fontSizes[index] = parseFloat(
            window.getComputedStyle(textElement).fontSize
          );
        }

        if (this.userData) {
          if (
            typeof this.userData.personalizationMode === 'undefined' ||
            this.userData.personalizationMode === 'fromType'
          ) {
            if (value.types?.length > 1) {
              textContainer.innerHTML = value.formula.replace(
                /\$([a-zA-Z]+)/g,
                (match, offset, str) => {
                  const replacement = '';
                  if (this.userData && this.userData[match.substring(1)]) {
                    return this.userData[match.substring(1)];
                  }
                  return '';
                }
              );
            } else {
              if (value.types && this.userData) {
                textContainer.innerHTML = this.userData[value.types[0]] ?? '';
              }
            }
          } else if (Array.isArray(this.userData?.lines)) {
            if (typeof value.lineNumber === 'undefined') {
              value.lineNumber = 1;
            }
            textContainer.innerHTML = this.userData.lines[value.lineNumber - 1];
          }
        }
        if (textContainer.nodeName !== 'textPath') {
          switch (value.align) {
            case 'Left':
              textElement.setAttribute('text-anchor', 'start');
              textContainers.item(index).setAttribute('x', String(value.posX));
              break;
            case 'Center':
              textContainers.item(index).setAttribute('text-anchor', 'middle');
              textElement.setAttribute('x', '50%');
              break;
            case 'Right':
              textElement.setAttribute('text-anchor', 'end');
              textContainers.item(index).setAttribute('x', String(value.posX));
              break;
          }
        }
        if (value.fontFluidity === 'Fluid' && value.textMaxSize) {
          textElement.style.fontSize = `${value.fontMaxSize - 1}px`;
          console.log(textElement.getBBox().width, ' VS ', value.textMaxSize);
          while (textElement.getBBox().width > value.textMaxSize) {
            const currentFontSize = parseInt(
              window.getComputedStyle(textElement).fontSize,
              10
            );
            textElement.style.fontSize = `${
              currentFontSize *
              Math.min(0.95, value.textMaxSize / textElement.getBBox().width)
            }px`;
            console.log('test');
          }
        }
        if (value.syncFontSize) {
          minFont = Math.min(
            parseInt(window.getComputedStyle(textElement).fontSize, 10),
            minFont
          );
          if (value.fontMaxSize) {
            minFont = Math.min(minFont, value.fontMaxSize);
          }
        }
      });
      if (minFont !== 999999) {
        textConfig.forEach((value, index) => {
          if (value.syncFontSize) {
            const textElement = textContainers.item(index);
            textElement.style.fontSize = String(minFont);
          }
        });
      }
    });
    combineLatest(allPreparations)
      .pipe(first())
      .subscribe(() => {
        this.fontLoaded.emit();
        console.log('MARK SVG AS LOADED');
        this.svgLoaded.emit();
      });
  }

  updateTextFields() {
    this.runTextFieldUpdate$.next(undefined);
  }
}

const svgCache = {};
